import { Children, useMemo, useState, type ReactNode } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $dashboardNotificationStore, $menuStore, $profileFeatureStore, $profileStore } from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { NotificationsModal } from '@widgets';
import { Button, Icon, Text } from '@shared/widgets';
import NotificationIcon from '@assets/icons/notification.svg';
import NotificationActiveIcon from '@assets/icons/notification-active.svg';
import ChevronIcon from '@assets/icons/chevron.svg';
import BurgerIcon from '@assets/icons/dashboard/burger.svg';
import BurgerDarkIcon from '@assets/icons/dashboard/burger-dark.svg';
import GroupIcon from '@assets/icons/group.svg';

interface HeaderProps {
  className?: string;
  return?: boolean;
  back?: boolean;
  backText?: string;
  to?: string | null;
  header?: ReactNode;
  showProfileName?: boolean;
  topRightAddons?: ReactNode;
  onShowMobileMenu?: () => void;
}

export const Header = (props: HeaderProps) => {
  const { back, navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const profileStore = useUnit($profileStore.store);
  const dashboardNotificationStore = useUnit($dashboardNotificationStore.store);
  const isCollapsedMenu = useUnit($menuStore.store);

  const [showNotificationsModal, setShowNotificationsModal] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const hasUnreadNotifications = useMemo(
    () => dashboardNotificationStore.data.some((notification) => !notification.isRead),
    [dashboardNotificationStore.data],
  );
  return (
    <header
      className={cn(
        'flex h-[70px] w-full shrink-0 items-center',
        'max-md:h-[44px] max-md:justify-between max-md:px-4',
        PaletteUtils.isInsigniaPalette &&
          'justify-between border-b border-b-gray-black bg-white px-6 dark:border-b-gray-wet-asphalt dark:bg-soft-black',
        PaletteUtils.isVcfcPalette && [
          'bg-ghost-white px-10 pb-1 dark:bg-eerie-black',
          'max-md:bg-white max-md:dark:bg-soft-black',
          props.return && 'items-center bg-white dark:bg-soft-black',
        ],
        props.className,
      )}
    >
      {props.back ? (
        <div className='group inline-flex cursor-pointer items-center gap-1' onClick={() => (props.to ? navigate(props.to) : back())}>
          <ChevronIcon />
          <Text className='group-hover:underline' size='sm'>
            {props.backText ?? t('layout.header.goBack')}
          </Text>
        </div>
      ) : (
        <>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Button
              className={cn(
                'dark:bg-bg-soft-black mr-6 h-8 w-8 shrink-0 p-0 hover:bg-midnight-graphite dark:border dark:border-primary',
                'max-md:mr-0 max-md:bg-transparent max-md:hover:bg-transparent max-md:focus:bg-transparent md:hidden',
              )}
              color='primary'
              onClick={props.onShowMobileMenu}
            >
              <Icon
                className='dark:text-primary'
                icon={
                  <>
                    <BurgerIcon className='dark:hidden' />
                    <BurgerDarkIcon className='hidden dark:block' />
                  </>
                }
              />
            </Button>
            {isCollapsedMenu && (
              <Button
                className={cn('mr-6 h-8 w-8 shrink-0 rounded-sm p-0', 'max-md:hidden', PaletteUtils.isVcfcPalette && 'rounded-3xl')}
                color='primary'
                onClick={() => $menuStore.events.toggleMenu()}
              >
                <Icon
                  className='rotate-180'
                  color={PaletteUtils.getColor('secondary', 'white')}
                  icon={<ChevronIcon className={cn(PaletteUtils.isVcfcPalette && 'dark:text-primary')} />}
                />
              </Button>
            )}
            <Text tag='h1' weight={PaletteUtils.isVcfcPalette ? 'medium' : 'semibold'} size={PaletteUtils.isVcfcPalette ? '3xl' : '2xl'}>
              {props.header}
            </Text>
          </div>
          <div className={cn('flex shrink-0 items-center gap-5', 'max-md:ml-0 max-md:gap-2', PaletteUtils.isVcfcPalette && 'ml-auto')}>
            {props.showProfileName && (
              <Text className={cn('text-right', 'max-md:max-w-[80px] max-md:text-xs')} weight='bold' truncate>
                {PaletteUtils.isPersonalPalette
                  ? `${profileStore.data?.firstName || ''} ${profileStore.data?.lastName || ''}`
                  : profileStore.data?.companyName || ''}
              </Text>
            )}
            <div className={cn('relative shrink-0', 'md:hidden')}>
              <Button onClick={() => setMobileMenuOpen((prev) => !prev)}>
                <Icon icon={<GroupIcon className='dark:text-white' />} />
              </Button>
              {mobileMenuOpen && (
                <div className='absolute right-0 top-full z-50 rounded-bl-md rounded-br-md bg-white py-2 dark:bg-soft-black'>
                  {profileFeatureStore.data?.settings.viewNotifications && (
                    <Button
                      className={cn('flex w-full items-center px-4 py-2 text-left', 'hover:bg-gray-100 dark:hover:bg-gray-700')}
                      onClick={() => (setShowNotificationsModal(true), setMobileMenuOpen(false))}
                    >
                      <Icon icon={hasUnreadNotifications ? <NotificationActiveIcon /> : <NotificationIcon />} />
                    </Button>
                  )}
                  <div className='flex'>
                    {props.topRightAddons &&
                      Children.map(props.topRightAddons, (child, idx) => (
                        <div
                          key={idx}
                          className={cn('px-4 py-2', 'hover:bg-gray-100 dark:hover:bg-gray-700')}
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
            <div className={cn('hidden items-center gap-5', 'md:flex')}>
              {profileFeatureStore.data?.settings?.viewNotifications && (
                <Button
                  className={cn('h-10 w-10 rounded-sm', PaletteUtils.isVcfcPalette && 'bg-light/10', 'hover:bg-light/60 dark:hover:bg-light/30')}
                  onClick={() => setShowNotificationsModal(true)}
                >
                  <Icon icon={hasUnreadNotifications ? <NotificationActiveIcon /> : <NotificationIcon />} />
                </Button>
              )}
              {props.topRightAddons && <div className='flex gap-2'>{props.topRightAddons}</div>}
            </div>
          </div>
        </>
      )}
      <NotificationsModal isOpen={showNotificationsModal} onClose={() => setShowNotificationsModal(false)} />
    </header>
  );
};
